import React from "react";
import styled from "styled-components";
import { style } from "./style";

const spaceVars = style.vars.space;
const borderVars = style.vars.border;
const borderRadiusVars = style.vars.borderRadius;

type Space = keyof typeof spaceVars;
type Display = "block" | "inline-block" | "inline";
type Border = keyof typeof borderVars;
type BorderRadius = keyof typeof borderRadiusVars;

type MarginProps = {
  /**
   * Whole margin on the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   * Partially overridden by mx, my, mt, mb, mr, ml.
   */
  m?: Space | string;

  /**
   * Horizontal margin on the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   * Partially overridden by mr, ml.
   */
  mx?: Space | string;
  /**
   * Vertical margin on the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   * Partially overridden by mt, mb.
   */
  my?: Space | string;
  /**
   * Top margin on the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  mt?: Space | string;
  /**
   * Bottom margin on the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  mb?: Space | string;
  /**
   * Right margin on the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  mr?: Space | string;
  /**
   * Left margin on the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  ml?: Space | string;
};

type PaddingProps = {
  /**
   * Whole padding inside the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   * Partially overridden by px, py, pt, pb, pr, pl.
   */
  p?: Space | string;

  /**
   * Horizontal padding inside the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   * Partially overridden by pr, pl.
   */
  px?: Space | string;
  /**
   * Vertical padding inside the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   * Partially overridden by pt, pb.
   */
  py?: Space | string;
  /**
   * Top padding inside the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  pt?: Space | string;
  /**
   * Bottom padding inside the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  pb?: Space | string;
  /**
   * Right padding inside the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  pr?: Space | string;
  /**
   * Left padding inside the Box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  pl?: Space | string;
};

type NonStaticPositionProps = {
  /**
   * Top, bottom, left, right values are used in boxes with non-static, i.e. non-default positions e.g. relative, absolute,
   * fixed, sticky. 
   * These have no effect on a box with default or static positioning.
   * The elements these values are relative to vary. See https://developer.mozilla.org/en-US/docs/Web/CSS/position
   * /
  /**
   * Position from the top of the box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  t?: Space | string;

  /**
   * Position from the bottom of the box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  b?: Space | string;
  /**
   * Position from the left of the box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  l?: Space | string;
  /**
   * Position from the right of the box. If a number 1-10 is used, it will
   * fallback to the style guide values for spacing; otherwise uses the string provided.
   */
  r?: Space | string;
};

type AccessibilityProps = {
  /**
   * Sets the base type of element used by the Box. Defaults to `div`.
   */
  as?: keyof JSX.IntrinsicElements;
  /**
   * Sets the `role` attribute on the Box element.
   */
  role?: React.AriaRole;
};

type DisplayProps = {
  /**
   * Sets the display type of the Box.
   */
  display?: Display;
  /**
   * Sets the position type of the Box.
   */
  position?: "absolute" | "relative" | "fixed" | "sticky";
};

type BorderProps = {
  /**
   * Sets the border around the Box. Valid inputs come from with the styleguide.
   */
  border?: Border;
  /**
   * Sets the border radius of the Box. Valid inputs come from with the styleguide.
   */
  borderRadius?: BorderRadius;
};

type DimensionsProps = {
  /**
   * Sets the width of the Box.
   */
  width?: string;
  /**
   * Sets the maximum width of the Box.
   */
  maxWidth?: string;
  /**
   * Sets the minimum width of the Box.
   */
  minWidth?: string;
  /**
   * Sets the height of the Box.
   */
  height?: string;
  /**
   * Sets the maximum height of the Box.
   */
  maxHeight?: string;
  /**
   * Sets the minimum height of the Box.
   */
  minHeight?: string;
};

type BackgroundProps = {
  /**
   * Sets the background of the Box.
   */
  background?: string;
};

type UnsafeProps = {
  /**
   * UNSAFE style overrides for the Box. If you're using this prop, you're probably
   * trying to override what is already implemented in Walrus. That's fine, but we prefix this
   * with UNSAFE so that's easy to grep for in the future.
   */
  UNSAFE_style?: React.CSSProperties;
  /**
   * UNSAFE style overrides for the Box. If you're using this prop, you're probably
   * trying to override what is already implemented in Walrus. That's fine, but we prefix this
   * with UNSAFE so that's easy to grep for in the future.
   */
  UNSAFE_className?: string;
};

type Props = React.PropsWithChildren<
  MarginProps &
    PaddingProps &
    NonStaticPositionProps &
    AccessibilityProps &
    DisplayProps &
    BorderProps &
    DimensionsProps &
    BackgroundProps &
    UnsafeProps
>;

const getSpace = (size?: Space | string) => {
  if (size === undefined) {
    return undefined;
  }

  if (typeof size === "string") {
    return size;
  }

  return spaceVars[size];
};

const marginStyle = (props: MarginProps) => {
  return {
    marginTop: getSpace(props.mt ?? props.my ?? props.m),
    marginBottom: getSpace(props.mb ?? props.my ?? props.m),
    marginLeft: getSpace(props.ml ?? props.mx ?? props.m),
    marginRight: getSpace(props.mr ?? props.mx ?? props.m),
  };
};

const paddingStyle = (props: PaddingProps) => {
  return {
    paddingTop: getSpace(props.pt ?? props.py ?? props.p),
    paddingBottom: getSpace(props.pb ?? props.py ?? props.p),
    paddingLeft: getSpace(props.pl ?? props.px ?? props.p),
    paddingRight: getSpace(props.pr ?? props.px ?? props.p),
  };
};

const nonStaticPositionStyle = (props: NonStaticPositionProps) => {
  return {
    top: getSpace(props.t),
    bottom: getSpace(props.b),
    left: getSpace(props.l),
    right: getSpace(props.r),
  };
};

const displayStyle = (props: DisplayProps) => {
  return {
    display: props.display ?? "inline-block",
    position: props.position ?? undefined,
  };
};

const borderStyle = (props: BorderProps) => {
  return {
    border: props.border ? borderVars[props.border] : undefined,
    borderRadius: props.borderRadius ? borderRadiusVars[props.borderRadius] : undefined,
  };
};

const dimensionsStyle = (props: DimensionsProps) => {
  return {
    width: props.width,
    maxWidth: props.maxWidth,
    minWidth: props.minWidth,
    height: props.height,
    maxHeight: props.maxHeight,
    minHeight: props.minHeight,
  };
};

const backgroundProps = (props: BackgroundProps) => {
  return {
    background: props.background,
  };
};

const Container = styled.div<Props>`
  all: unset;
  box-sizing: border-box;

  ${marginStyle}
  ${paddingStyle}
  ${nonStaticPositionStyle}
  ${displayStyle}
  ${borderStyle}
  ${dimensionsStyle}
  ${backgroundProps}
`;

export const Box = React.forwardRef<HTMLElement, Props>((props, ref) => {
  /**
   * Do not use object spread `{ ...props }` here to avoid
   * injecting additional properties into the container.
   */
  return (
    <Container
      ref={ref}
      as={props.as}
      role={props.role}
      m={props.m}
      mx={props.mx}
      my={props.my}
      mt={props.mt}
      mb={props.mb}
      mr={props.mr}
      ml={props.ml}
      p={props.p}
      px={props.px}
      py={props.py}
      pt={props.pt}
      pb={props.pb}
      pr={props.pr}
      pl={props.pl}
      t={props.t}
      b={props.b}
      l={props.l}
      r={props.r}
      display={props.display}
      position={props.position}
      border={props.border}
      borderRadius={props.borderRadius}
      width={props.width}
      maxWidth={props.maxWidth}
      minWidth={props.minWidth}
      height={props.height}
      maxHeight={props.maxHeight}
      minHeight={props.minHeight}
      background={props.background}
      className={props.UNSAFE_className}
      style={props.UNSAFE_style}
    >
      {props.children}
    </Container>
  );
});

Box.displayName = "Box";
